import * as React from "react";
import { Link } from "react-router-dom";
import { Gem, Sparkles } from "lucide-react";

import { getEntitlements } from "@/api/client";
import { EntitlementsView } from "@/api/types";

/**
 * A compact "Your plan" card for the profile sidebar: the effective tier, the
 * trial countdown while it's live, and per-quota usage bars. Reads
 * GET /entitlements — the same numbers the @RequiresQuota gate enforces, so what
 * you see is what you get.
 */
/**
 * Labels for the quotas worth showing a host.
 *
 * A quota with no entry here is not rendered, which is deliberate: MONTHLY_BOOKINGS is counted but
 * nothing enforces it and it is on no plan surface, so a usage bar for it would be the one number on
 * this card that means nothing. Give it a label the day something gates it.
 *
 * The key is AVAILABILITY_PATTERNS. It was MEETING_TYPES until the model was renamed, and this map
 * was missed, so the card fell through to `?? q.key` and showed hosts the raw enum name.
 */
const QUOTA_LABELS: Record<string, string> = {
  AVAILABILITY_PATTERNS: "Availability patterns",
};

export function PlanUsagePanel() {
  const [ent, setEnt] = React.useState<EntitlementsView | null>(null);

  React.useEffect(() => {
    getEntitlements()
      .then(setEnt)
      .catch(() => {
        /* non-critical sidebar card — stay hidden on failure */
      });
  }, []);

  if (!ent) return null;

  return (
    <section className="rounded-2xl border-l-4 border-brand-teal bg-muted/20 p-4">
      <h2 className="mb-3 flex items-center gap-2 font-comic-headline text-sm uppercase tracking-wide text-muted-foreground">
        <Gem className="h-4 w-4" /> Your plan
      </h2>

      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-brand-blue px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-brand-blue-foreground">
          {ent.tier}
        </span>
        {ent.trialActive && ent.trialEndsAt && (
          <span className="text-[11px] font-medium text-warning-strong">
            Trial · {daysLeft(ent.trialEndsAt)} day{daysLeft(ent.trialEndsAt) === 1 ? "" : "s"} left
          </span>
        )}
      </div>

      <ul className="mt-3 space-y-2.5">
        {ent.quotas.filter((q) => QUOTA_LABELS[q.key] != null).map((q) => {
          const pct =
            !q.unlimited && q.limit && q.limit > 0 ? Math.min(100, (q.used / q.limit) * 100) : 0;
          const atLimit = !q.unlimited && q.limit != null && q.used >= q.limit;
          return (
            <li key={q.key}>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">{QUOTA_LABELS[q.key]}</span>
                <span className={"tabular-nums " + (atLimit ? "font-semibold text-warning-strong" : "text-muted-foreground")}>
                  {q.unlimited ? `${q.used} · ∞` : `${q.used} / ${q.limit}`}
                </span>
              </div>
              {!q.unlimited && (
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-foreground/10">
                  <div
                    className={"h-full rounded-full transition-[width] " + (atLimit ? "bg-warning" : "bg-brand-blue")}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Free/Basic get the upgrade CTA; Pro/Enterprise get a quiet "change plan" link —
          moving down from a top tier shouldn't read as a call to action. */}
      {ent.tier === "FREE" || ent.tier === "BASIC" ? <UpgradeCta /> : <ChangePlanLink />}
    </section>
  );
}

/** The upsell for Free/Basic — a light palette gradient (inviting, not heavy). */
function UpgradeCta() {
  return (
    <Link
      to="/upgrade"
      className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-brand-blue/40 bg-gradient-to-r from-brand-blue/15 via-brand-teal/15 to-brand-coral/15 px-4 py-2.5 font-display text-sm font-semibold text-foreground transition hover:from-brand-blue/25 hover:via-brand-teal/25 hover:to-brand-coral/25"
    >
      <Sparkles className="h-4 w-4 text-brand-blue" />
      Upgrade your plan
    </Link>
  );
}

/** For Pro/Enterprise: a quiet grey link, not a CTA (changing plan may be a downgrade). */
function ChangePlanLink() {
  return (
    <Link
      to="/upgrade"
      className="mt-4 block text-xs text-muted-foreground underline-offset-2 transition hover:text-foreground hover:underline"
    >
      Change your plan
    </Link>
  );
}

function daysLeft(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
}
